import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, RotateCcw } from "lucide-react";
import * as flow from "@/lib/flow";

export default function OperatingState({ result, content }) {
  const meta = content?.operating_state_meta;
  const states = content?.operating_states || [];
  const [phase, setPhase] = useState("choose"); // choose | unsure | none | done
  const [selected, setSelected] = useState(null); // state object | {id:'unsure'} | {id:'none'}
  const [focus, setFocus] = useState(null);
  const [custom, setCustom] = useState("");

  if (!meta || !states.length) return null;

  const save = (payload) => { flow.saveOperatingState(result, payload).catch(() => {}); };

  const pickState = (s) => {
    setSelected(s);
    setPhase("done");
    save({ state: s.id, skipped: false });
  };

  const pickFocus = (f) => {
    setFocus(f);
    setSelected({ id: "unsure", label: "I'm not sure" });
    setPhase("done");
    save({ state: "unsure", focus: f.id, skipped: false });
  };

  const saveCustom = () => {
    if (!custom.trim()) return;
    setSelected({ id: "none", label: "In your words" });
    setPhase("done");
    save({ state: "none", custom_text: custom.trim(), skipped: false });
  };

  const skip = () => {
    setPhase("skipped");
    save({ skipped: true });
  };

  const change = () => {
    setSelected(null); setFocus(null); setCustom(""); setPhase("choose");
  };

  if (phase === "skipped") {
    return (
      <div className="px-6 mt-8">
        <button data-testid="os-reopen" onClick={change}
          className="text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors inline-flex items-center gap-1.5">
          <RotateCcw size={14} strokeWidth={1.6} /> Give language to your state
        </button>
      </div>
    );
  }

  return (
    <div className="px-6 mt-8">
      <div className="ss-card p-5" data-testid="operating-state">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-[#6B6B6B]">
            {meta.intro_title}
          </span>
          <span className="text-[11px] text-[#9a9a94]">{meta.optional_note}</span>
        </div>

        <AnimatePresence mode="wait">
          {phase === "choose" && (
            <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[#474747]">{meta.intro_body}</p>
              <p className="mt-4 text-[14.5px] font-medium">{meta.prompt}</p>
              <div className="mt-3 space-y-2">
                {states.map((s) => (
                  <button key={s.id} data-testid={`os-option-${s.id}`} onClick={() => pickState(s)}
                    className="w-full text-left rounded-xl border border-[#EBE9E4] bg-white p-3.5 ss-tap ss-focus hover:border-[#C9A227] transition-colors">
                    <div className="text-[15px] font-medium">{s.label}</div>
                    <div className="mt-0.5 text-[13px] text-[#6B6B6B]">{s.summary}</div>
                  </button>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid="os-extra-unsure" onClick={() => setPhase("unsure")}
                  className="rounded-full border border-[#EBE9E4] bg-white px-4 py-2 text-[13px] ss-tap ss-focus">
                  {meta.extra_options[0].label}
                </button>
                <button data-testid="os-extra-none" onClick={() => setPhase("none")}
                  className="rounded-full border border-[#EBE9E4] bg-white px-4 py-2 text-[13px] ss-tap ss-focus">
                  {meta.extra_options[1].label}
                </button>
              </div>
              <button data-testid="os-skip" onClick={skip}
                className="mt-4 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors">
                Skip this
              </button>
            </motion.div>
          )}

          {phase === "unsure" && (
            <motion.div key="unsure" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p className="mt-3 text-[14px] leading-relaxed text-[#474747]">{meta.unsure_prompt}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {meta.unsure_options.map((f) => (
                  <button key={f.id} data-testid={`os-focus-${f.id}`} onClick={() => pickFocus(f)}
                    className="rounded-full border border-[#EBE9E4] bg-white px-4 py-2.5 text-[13.5px] ss-tap ss-focus hover:border-[#C9A227] transition-colors">
                    {f.label}
                  </button>
                ))}
              </div>
              <button data-testid="os-back-choose" onClick={() => setPhase("choose")}
                className="mt-4 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors">
                Back
              </button>
            </motion.div>
          )}

          {phase === "none" && (
            <motion.div key="none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <p className="mt-3 text-[14px] leading-relaxed text-[#474747]">{meta.none_prompt}</p>
              <input data-testid="os-custom-input" value={custom} onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveCustom()}
                placeholder="In a few words…"
                className="mt-3 w-full rounded-xl border border-[#EBE9E4] bg-white p-3.5 text-[15px] ss-focus" />
              <div className="mt-3 flex gap-2">
                <button data-testid="os-custom-save" onClick={saveCustom} disabled={!custom.trim()}
                  className="rounded-full bg-[#1A1A1A] text-[#FDFBF7] px-5 py-2.5 text-[13.5px] font-medium ss-tap ss-focus disabled:opacity-50">
                  Save my words
                </button>
                <button data-testid="os-back-choose-2" onClick={() => setPhase("choose")}
                  className="text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus px-2">
                  Back
                </button>
              </div>
            </motion.div>
          )}

          {phase === "done" && (
            <motion.div key="done" initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} data-testid="os-selected">
              <div className="mt-3 flex items-center gap-2">
                <span className="grid place-items-center h-6 w-6 rounded-full bg-[#F7F2D7] flex-shrink-0">
                  <Check size={14} strokeWidth={2.5} className="text-[#8a6d10]" />
                </span>
                <p className="text-[14.5px]">
                  You selected <span className="font-semibold">{selected?.label}</span>
                  {focus ? ` · ${focus.label.toLowerCase()}` : ""}
                </p>
              </div>
              {selected?.meaning && (
                <p className="mt-3 text-[13.5px] leading-relaxed text-[#474747]">{selected.meaning}</p>
              )}
              {selected?.action && (
                <div className="mt-3 rounded-xl border border-[#EBE9E4] bg-[#FBFAF6] p-3.5">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8a6d10]">
                    What may help next · {selected.action}
                  </div>
                  <p className="mt-1 text-[13.5px] leading-relaxed text-[#474747]">{selected.action_hint}</p>
                </div>
              )}
              {custom && (
                <p className="mt-3 text-[13.5px] italic leading-relaxed text-[#474747]">“{custom}”</p>
              )}
              <button data-testid="os-change" onClick={change}
                className="mt-4 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors inline-flex items-center gap-1.5">
                <RotateCcw size={13} strokeWidth={1.6} /> Change or remove
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <p className="mt-2 px-1 text-[11.5px] leading-relaxed text-[#9a9a94]">{meta.disclaimer}</p>
    </div>
  );
}
